import { useState } from "react";
import { Mail, CheckCircle2, Truck, Cable, PencilRuler, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/apiClient";
import { SectionHeading } from "./SectionHeading";

const INTERESTS = [
  {
    value: "LOCATION",
    label: "Location de matériel",
    desc: "Disponibilité nacelle, groupes électrogènes, nouveaux matériels",
    icon: Truck,
    color: "text-[#F59E0B]",
    bg: "bg-[#FBBF24]/10",
    border: "border-[#FBBF24]",
  },
  {
    value: "ELECTRIQUE_IRVE",
    label: "Électricité / IRVE",
    desc: "Installation de bornes, maintenance, dépannage",
    icon: Cable,
    color: "text-[#0F76B8]",
    bg: "bg-[#0F76B8]/10",
    border: "border-[#0F76B8]",
  },
  {
    value: "DAO",
    label: "DAO / dossiers",
    desc: "Plans, extensions, dossiers administratifs, études électriques",
    icon: PencilRuler,
    color: "text-[#3DAE2B]",
    bg: "bg-[#3DAE2B]/10",
    border: "border-[#3DAE2B]",
  },
];

export function NewsletterSection() {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [selected, setSelected] = useState([]);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const toggle = (v) =>
    setSelected((s) => (s.includes(v) ? s.filter((x) => x !== v) : [...s, v]));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Merci d'accepter de recevoir nos communications.");
      return;
    }
    if (selected.length === 0) {
      toast.error("Sélectionnez au moins un centre d'intérêt.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", {
        email: email.trim(),
        first_name: firstName.trim() || null,
        interests: selected,
        consent: true,
      });
      setDone(true);
      toast.success(data.message || "Inscription confirmée !");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Une erreur est survenue, réessayez.";
      toast.error(typeof msg === "string" ? msg : "Erreur d'inscription");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="bg-white border-y border-slate-200"
      data-testid="newsletter-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
        <SectionHeading
          eyebrow="Newsletter"
          title="Restez informé de nos actualités"
          subtitle="Recevez en avant-première la disponibilité de nos matériels, nos nouveautés en installation IRVE et nos actualités DAO. Pas de spam, jamais"
          testId="newsletter-heading"
        />

        <div className="mt-14 max-w-4xl mx-auto">
          {done ? (
            <div
              className="rounded-3xl border-2 border-[#3DAE2B]/30 bg-[#3DAE2B]/5 p-10 text-center"
              data-testid="newsletter-success"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#3DAE2B] mb-4">
                <CheckCircle2 className="w-9 h-9 text-white" />
              </div>
              <h3 className="font-display text-2xl font-bold text-slate-900">
                Merci pour votre inscription !
              </h3>
              <p className="mt-2 text-slate-600">
                Vous recevrez bientôt nos actualités sur les thèmes choisis.
              </p>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="rounded-3xl border border-slate-200 bg-slate-50 p-6 sm:p-10 shadow-sm"
              data-testid="newsletter-form"
            >
              {/* Interests grid */}
              <div>
                <div className="text-sm font-semibold text-slate-900 mb-4">
                  Quels sujets vous intéressent ?{" "}
                  <span className="text-slate-500 font-normal">
                    (sélection multiple)
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {INTERESTS.map((it) => {
                    const active = selected.includes(it.value);
                    return (
                      <button
                        key={it.value}
                        type="button"
                        onClick={() => toggle(it.value)}
                        data-testid={`newsletter-interest-${it.value.toLowerCase()}`}
                        className={`text-left rounded-2xl border-2 p-4 transition-all ${
                          active
                            ? `${it.border} ${it.bg} shadow-md`
                            : "border-slate-200 bg-white hover:border-slate-300"
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className={`w-10 h-10 rounded-xl ${it.bg} flex items-center justify-center`}>
                            <it.icon className={`w-5 h-5 ${it.color}`} strokeWidth={2.2} />
                          </div>
                          <div
                            className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                              active
                                ? "border-[#1B3A6B] bg-[#1B3A6B]"
                                : "border-slate-300 bg-white"
                            }`}
                          >
                            {active && <CheckCircle2 className="w-4 h-4 text-white" strokeWidth={3} />}
                          </div>
                        </div>
                        <div className="mt-3 font-semibold text-slate-900 text-sm">
                          {it.label}
                        </div>
                        <div className="mt-1 text-xs text-slate-600 leading-relaxed">
                          {it.desc}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Fields */}
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="nl-fname" className="text-xs font-medium text-slate-600 mb-1 block">
                    Prénom <span className="text-slate-400">(optionnel)</span>
                  </label>
                  <input
                    id="nl-fname"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Jean"
                    data-testid="newsletter-firstname"
                    className="w-full h-11 px-3 rounded-xl bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30 focus:border-[#1B3A6B]"
                  />
                </div>
                <div>
                  <label htmlFor="nl-email" className="text-xs font-medium text-slate-600 mb-1 block">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="nl-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="vous@exemple.com"
                    data-testid="newsletter-email"
                    className="w-full h-11 px-3 rounded-xl bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30 focus:border-[#1B3A6B]"
                  />
                </div>
              </div>

              {/* Consent */}
              <label className="mt-5 flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="newsletter-consent"
                  className="mt-0.5 w-4 h-4 rounded border-slate-300 text-[#1B3A6B] focus:ring-[#1B3A6B]/30"
                />
                <span className="text-xs text-slate-600 leading-relaxed">
                  J&apos;accepte de recevoir par email les actualités et
                  communications d&apos;INGélec relatives aux sujets sélectionnés.
                  Je peux me désinscrire à tout moment via le lien présent dans
                  chaque email ou sur la page{" "}
                  <a
                    href="/desinscription"
                    className="underline hover:text-[#1B3A6B] font-medium"
                  >
                    /desinscription
                  </a>
                  .
                </span>
              </label>

              <button
                type="submit"
                disabled={busy}
                data-testid="newsletter-submit"
                className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-[#1B3A6B] hover:bg-[#142c54] text-white font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-blue-900/20"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Inscription…
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" /> Je m&apos;inscris
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

export default NewsletterSection;
